import { mainItemTypeValues } from 'default-settings';
import { notForPreview } from 'widget/utils/not-for-preview';
import autobind from 'core-decorators/lib/autobind';

import WixSDK from 'shared/utils/wix-sdk';
import { sendPerformanceInfo, addSingleMeasurement } from 'shared/utils/performance';
import paymentsHelper from 'widget/utils/payments';
import performanceTypes from 'shared/utils/performance/types';
import { canPlayFullOrPreview } from 'widget/ui-selectors/video-access';
import { getOpenedEventParams } from 'shared/utils/bi/video-overlay-events-params';
import { playerSize } from 'widget/utils/videos-sizes';
import bi from 'shared/bi/bi';

import { openFullScreenOverlay } from 'shared/utils/open-overlay';
import { closeModal } from 'shared/components/modal/modal';
import { requestLogin } from 'shared/utils/request-login';
import { withBiEvent } from 'shared/bi/send-bi-event';

import events from 'shared/constants/events';
import viewModes from 'shared/constants/view-modes';
import SEARCH_QUERY_FIELDS from 'shared/constants/search-query-fields';

import Spinner from 'shared/components/spinner/loading-spinner';
import ResizedImage from 'shared/components/resized-image/resized-image';
import NoVideosContent from 'widget/components/no-content/no-videos-content/no-videos-content';
import { openPurchase, openSubscription } from 'widget/components/payments/payments';


export default class BaseView extends React.Component {

  static propTypes = {
    mainVideoId: React.PropTypes.string.isRequired,
    isVideoPlaying: React.PropTypes.bool.isRequired,
    playVideo: React.PropTypes.func.isRequired,
    pauseVideo: React.PropTypes.func.isRequired,
    selectVideo: React.PropTypes.func.isRequired,
    resetSelectedVideo: React.PropTypes.func.isRequired,
    currentChannelId: React.PropTypes.string.isRequired,
    canShowChannelCover: React.PropTypes.bool.isRequired,
    prevVideoId: React.PropTypes.string,
    nextVideoId: React.PropTypes.string,
    channelData: React.PropTypes.object.isRequired,
    videoByIds: React.PropTypes.object.isRequired,
    fetchVideos: React.PropTypes.func.isRequired,
    isVideosFetching: React.PropTypes.bool.isRequired,
    channel: React.PropTypes.object.isRequired,
    currentSiteUser: React.PropTypes.object.isRequired,
    isCurrentSiteUserFetching: React.PropTypes.bool,
    channelVideo: React.PropTypes.object.isRequired,
    channelPaymentData: React.PropTypes.object.isRequired,
    videoIdsByPageNumber: React.PropTypes.array,
    appSettings: React.PropTypes.object.isRequired,
    updateSearch: React.PropTypes.func,
    resetSearch: React.PropTypes.func,
    searchQuery: React.PropTypes.string,
    searchQueryField: React.PropTypes.string,
    biEvent: React.PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.isOpenedBiSent = false;
  }

  componentDidMount() {
    this.updateInitialVideosLoadedState();
    // TODO: move to HOC after moving canShowChannelCover and paused to Redux Store
    WixSDK.addEventListener(WixSDK.Events.SETTINGS_UPDATED, this.reloadOnDashboardClosing);
    WixSDK.addEventListener(WixSDK.Events.PAGE_NAVIGATION, this.stopPlaybackOnPageChange);
    WixSDK.addEventListener(WixSDK.Events.EDIT_MODE_CHANGE, this.reset);
    WixSDK.addEventListener(WixSDK.Events.EDIT_MODE_CHANGE, closeModal);
  }

  componentWillReceiveProps(nextProps) {
    this.updateInitialVideosLoadedState(nextProps);

    if (WixSDK.Utils.getViewMode() === viewModes.EDITOR && this.hasChangesInAppSettings(nextProps)) {
      this.reset(nextProps);
    }
  }

  componentDidUpdate(prevProps) {
    const { currentChannelId, mainVideoId } = this.props;

    this.logOpenedBIEvent();

    // TODO: move to HOC after move canShowChannelCover to Redux Store
    if (currentChannelId !== prevProps.currentChannelId) {
      this.reset();
    }

    if (prevProps.mainVideoId && mainVideoId !== prevProps.mainVideoId) {
      bi.logWidgetSystem('video.select.rendered', { previousEventName: 'video.select.requested' });
    }
  }

  componentWillUnmount() {
    WixSDK.removeEventListener(WixSDK.Events.SETTINGS_UPDATED, this.reloadOnDashboardClosing);
    WixSDK.removeEventListener(WixSDK.Events.PAGE_NAVIGATION, this.stopPlaybackOnPageChange);
    WixSDK.removeEventListener(WixSDK.Events.EDIT_MODE_CHANGE, this.reset);
    WixSDK.addEventListener(WixSDK.Events.EDIT_MODE_CHANGE, closeModal);
  }

  hasChangesInAppSettings(nextProps) {
    const { booleans: nextBooleans, numbers: nextNumbers } = nextProps.appSettings;
    const { booleans: thisBooleans, numbers: thisNumbers } = this.props.appSettings;
    return (
      !_.isEqual(nextBooleans, thisBooleans) || !_.isEqual(nextNumbers, thisNumbers)
    );
  }

  updateInitialVideosLoadedState(props = this.props) {
    if (!this.state.isInitialVideosLoaded && !props.isVideosFetching) {
      this.setState({ isInitialVideosLoaded: true });
    }
  }

  get playerSize() {
    return playerSize(this.props.appSettings.requestedWidgetSize.width);
  }

  get mainVideo() {
    const { mainVideoId, videoByIds } = this.props;
    return videoByIds[mainVideoId];
  }

  permitSearch(searchQuery, searchQueryField = SEARCH_QUERY_FIELDS.QUERY) {
    this.props.updateSearch({ searchQuery, searchQueryField });
  }

  get isCategorySelected() {
    return this.props.searchQueryField === SEARCH_QUERY_FIELDS.CATEGORY;
  }

  showSearchLayout() {
    this.setState({ searchLayout: true });
  }

  logOpenedBIEvent() {
    const { appSettings, channelData } = this.props;
    const videoItem = this.mainVideo;

    if (!this.isUIBlockingDataLoaded || this.isOpenedBiSent) {
      return;
    }

    this.isOpenedBiSent = true;

    _.delay(() => {
      addSingleMeasurement(performanceTypes.appFullyRendered);
      sendPerformanceInfo();

      this.props.biEvent(
        'widget.opened',
        getOpenedEventParams(appSettings, channelData, videoItem, this.canShowChannelCover),
      );
    });
  }

  storeDataForReload() {
    return null;
  }

  // TODO: move to channel action callbacks after moving storeDataForReload to data-component
  @autobind
  @withBiEvent('widget.signIn.clicked')
  @notForPreview
  showLogInForm() {
    requestLogin({ isLogin: true })
      .then(this.storeDataForReload);
  }

  // TODO: move to channel action callbacks after moving storeDataForReload to data-component
  @autobind
  logOut() {
    WixSDK.logOutCurrentMember();
    this.storeDataForReload();
  }

  @autobind
  openAccountInfoAtOverlay() {
    const { channelData } = this.props;
    openFullScreenOverlay(`#/account-info/${channelData.id}`);
  }

  @autobind
  clearSearch() {
    const { currentCategory } = this.state;

    if (currentCategory) {
      this.permitSearch(currentCategory, SEARCH_QUERY_FIELDS.CATEGORY);
    } else {
      this.permitSearch('');
    }

    this.setSearchBarValue('');
  }

  @autobind
  searchByQuery(searchQuery) {
    bi.logWidgetSystem('videoList.searchByQuery.requested');
    this.permitSearch(searchQuery, SEARCH_QUERY_FIELDS.QUERY);
    this.setState({
      searchBarValue: searchQuery,
      currentCategory: null,
    });
  }

  @autobind
  setSearchBarValue(searchBarValue) {
    this.setState({ searchBarValue });
  }

  @autobind
  searchByCategory(currentCategory) {
    bi.logWidgetSystem('videoList.searchByCategory.requested');
    this.permitSearch(currentCategory, SEARCH_QUERY_FIELDS.CATEGORY);
    this.setState({
      currentCategory,
      searchBarValue: '',
    });
  }

  @autobind
  searchByTag(tag) {
    bi.logWidgetSystem('videoList.searchByTag.requested');
    this.setTagsFocusState(false);
    this.permitSearch(tag, SEARCH_QUERY_FIELDS.TAG);
    this.setState({
      searchBarValue: tag,
      currentCategory: null,
    });
    this.sendTagClickedBI();
  }

  @autobind
  handleTagsFocus() {
    this.setTagsFocusState(true);
  }

  @autobind
  handleTagsBlur() {
    this.setTagsFocusState(false);
  }

  setTagsFocusState(isFocused) {
    this.setState({
      isTagFocused: isFocused,
    });

    if (!isFocused) {
      setTimeout(() => this.hideSearchLayout(), 0);
    }
  }

  @autobind
  handleSearchInputFocus() {
    this.setSearchInputFocusState(true);
  }

  @autobind
  handleSearchInputBlur() {
    this.setSearchInputFocusState(false);
  }

  setSearchInputFocusState(isFocused) {
    this.setState({
      isSearchInputFocused: isFocused,
    });

    if (isFocused) {
      this.showSearchLayout();
    } else {
      if (this.hasNoSearchResults) {
        this.clearSearch();
      }
      setTimeout(() => this.hideSearchLayout(), 0);
    }
  }

  get hasNoSearchResults() {
    const { videoIdsByPageNumber, searchQuery, isVideosFetching } = this.props;
    return !isVideosFetching && searchQuery && !videoIdsByPageNumber.length;
  }

  hideSearchLayout() {
    const { isTagFocused, isSearchInputFocused } = this.state;
    if (!isTagFocused && !isSearchInputFocused) {
      this.setState({
        searchLayout: false,
      });
    }
  }

  sendTagClickedBI() {
    this.props.biEvent('widget.tags.clicked');
  }

  @autobind
  openPurchase() {
    openPurchase((this.mainVideo).id);
  }

  @autobind
  openSubscription() {
    openSubscription(this.props.biEvent);
  }

  @autobind
  selectVideo({ id }) {
    this.props.selectVideo(id);
  }

  @autobind
  playSelectedVideo() {
    const { mainVideoId: id } = this.props;

    this.playVideo({ id });
  }

  @autobind
  playPrevVideo() {
    const { prevVideoId: id } = this.props;

    if (id) {
      this.playVideo({ id });
    }
  }

  @autobind
  playNextVideo() {
    const { nextVideoId: id } = this.props;

    if (id) {
      this.playVideo({ id });
    }
  }

  playVideo({ id }) {
    const { videoByIds, channelData, currentSiteUser, selectVideo, playVideo } = this.props;
    const videoItem = videoByIds[id];
    const isPlayAllowed = canPlayFullOrPreview(channelData, videoItem, currentSiteUser);

    if (isPlayAllowed) {
      playVideo(videoItem.id);
    } else {
      selectVideo(videoItem.id);
    }
  }

  @autobind
  selectNextVideo() {
    const id = this.props.nextVideoId;

    if (id) {
      this.selectVideo({ id });
    }
  }

  @autobind
  selectPrevVideo() {
    const id = this.props.prevVideoId;

    if (id) {
      this.selectVideo({ id });
    }
  }

  get canShowChannelCover() {
    return this.props.canShowChannelCover;
  }

  get isMainItemChannelCover() {
    return this.props.appSettings.numbers.mainItemType === mainItemTypeValues.CHANNEL_COVER;
  }

  get isChannelCoverShown() {
    return this.canShowChannelCover && this.isMainItemChannelCover;
  }

  // TODO: move to redux store
  // https://jira.wixpress.com/browse/VOD-292
  @autobind
  setFullScreenState(isInFullScreen) {
    // in full screen in mobile the innerWidth becomes bigger that is should be, so exit.
    // Seems like this is viewer issue because site itself becomes wider than 320px.
    this.setState({ isInFullScreen });
  }

  reloadData() {
    const { channel, channelPaymentData, fetchVideos } = this.props;

    this.setState({
      isInitialVideosLoaded: false,
    });
    channel.fetch();
    channelPaymentData.fetch();
    fetchVideos();
  }

  // TODO: rename to cancelSubscription
  @autobind
  processSubscriptionCancel() {
    const { appSettings: { siteUrl, instance }, currentChannelId } = this.props;
    bi.logWidgetSystem('channel.unsubscribe.requested');
    paymentsHelper.cancelSubscription(currentChannelId, siteUrl, instance);
  }

  @autobind
  reset() {
    const { resetSearch, resetSelectedVideo } = this.props;

    resetSearch();
    resetSelectedVideo();
  }

  @autobind
  reloadOnDashboardClosing(event) {
    if (event === events.DASHBOARD.CLOSED) {
      this.reloadData();
      this.reset();
    }
  }

  @autobind
  setCurrentVideoFromPayment(paymentEvent) {
    if (paymentEvent.itemId) {
      this.selectVideo({
        id: paymentEvent.itemId,
      });
    }
  }

  @autobind
  stopPlaybackOnPageChange({ wasAppOnPage }) {
    if (wasAppOnPage && this.props.isVideoPlaying) {
      this.props.pauseVideo();
    }
  }

  get noVideoPlayerOverlay() {
    return (
      <div />
    );
  }

  get noVideosPlayerContent() {
    const { channelData } = this.props;
    const { width, height } = this.playerSize;

    if (!this.isMainItemChannelCover) {
      return (
        <NoVideosContent width={width}
                         height={height} />
      );
    }

    const coverUrl = channelData.customCoverUrl || channelData.coverUrl;
    return (
      <ResizedImage styleName="overlay-only"
                    src={coverUrl}
                    width={width}
                    height={height}>
        {this.noVideoPlayerOverlay}
      </ResizedImage>
    );
  }

  get isUIBlockingDataLoaded() {
    const { channelData, isCurrentSiteUserFetching, channelPaymentData, channelVideo } = this.props;

    return !_.isEmpty(channelData) &&
      !isCurrentSiteUserFetching &&
      !channelPaymentData.isFetching &&
      !channelVideo.isFetching &&
      this.state.isInitialVideosLoaded;
  }

  get spinner() {
    return (
      <div styleName="spinner-container">
        <Spinner styleName="spinner" />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/views/_base.js